import React, { useCallback } from 'react';
import { Box, Divider, LinkV2, styled, TextV2, useTheme } from '@withjoy/joykit';
import { useHistory } from '@shared/core';
import {
  EventTravelListFragment,
  EventTravelNoteFragment,
  FerryFragment,
  FlightFragment,
  HotelFragment,
  RentalCarFragment,
  RentalHomeFragment,
  ShuttleFragment,
  TaxiFragment,
  TentFragment,
  TrainFragment,
  TravelType,
  EventTravelBookingTypeFragment,
} from '@graphql/generated';
import {
  Assistance as TravelBookingAssistantIcon,
  CampFire as TravelCampingIcon,
  Car as TravelCarIcon,
  Plane as TravelFlightIcon,
  Sign as TravelGuideIcon,
  HotelProduct as TravelHotelIcon,
  House as TravelHouseIcon,
  Note as TravelNoteIcon,
  Ferry as TravelFerryIcon,
  Shuttle as TravelShuttleIcon,
  Taxi as TravelTaxiIcon,
  Train as TravelTrainIcon,
  Calendar,
  Clock,
} from '@withjoy/joykit/icons';
import createSvgIcon from '@shared/joykit/packages/core/icons/createSvgIcon';
import { InfoBlock } from '../../primitives/InfoBlock';
import { DiscountCodeBlock, LinkBlock, ListItemBlock, MapBlock, PhoneBlock } from '../../components/ItineraryBlocks';
import { PrettyLink } from '../../components/PrettyLink';
import { Flex } from '@withjoy/joykit';
import { useEventLocale } from '@shared/core/i18n/LocaleContext';
import { formatDateForLanguage } from '@shared/hooks/useFormattedDate/dateFormatter';
import Booking from './components/Booking';
import { prettyUrl } from '@shared/utils/formatting/prettyUrl/prettyUrl.formatter';
import { useTravelListTelemetry } from './TravelList.telemetry';
import { ReactComponent as SearchIcon } from '@assets/icons/search.svg';
import { ThriftyLogo, DollarLogo } from '@assets/icons';
import { ReactComponent as HertzLogo64 } from '@assets/icons/hertz.svg';
import { useTranslation } from '@shared/core';
import { SafeLink } from '@shared/components/SafeLink';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import {
  body2ToParagraphOverride,
  body3ToParagraphOverride,
  label3ToCaptionOverride,
} from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { withWindow } from '@shared/utils/withWindow';
import { useColorPaletteProvider } from '../../layouts/ColorPaletteProvider/ColorPaletteProvider';
import { STANDARD_TRANSITION } from '@shared/utils/animationTransition';
import { useLayout } from '@shared/context/LayoutProvider';

export const RENTAL_CAR_PROMOTION_LINKS: Record<'hertz' | 'oldHertz2' | 'oldHertz' | 'thrifty' | 'dollar', string> = {
  hertz: 'https://www.hertz.com/us/en/deals-and-offers/joy-everyday-savings',
  // Legacy Hertz URLs kept for backward compatibility - the system identifies rental car promotions
  // by matching URLs against these values rather than using a dedicated travel item type
  oldHertz2: 'https://www.hertz.com/rentacar/rental-car-deals/joy',
  oldHertz: 'https://withjoy.com/partner/hertz',
  thrifty: 'https://www.thrifty.com/us/en?CorporateDiscountNumber=3077704',
  dollar: 'https://www.dollar.com/?corpdiscnbr=3077703',
} as const;
export const isRentalCarPromotionLink = (url: string): boolean => {
  return Object.values(RENTAL_CAR_PROMOTION_LINKS).includes(url);
};

export const travelTypeToIcon: Readonly<Record<TravelType, ReturnType<typeof createSvgIcon>>> = {
  [TravelType.booking]: TravelBookingAssistantIcon,
  [TravelType.list]: TravelGuideIcon,
  [TravelType.flight]: TravelFlightIcon,
  [TravelType.ferry]: TravelFerryIcon,
  [TravelType.hotel]: TravelHotelIcon,
  [TravelType.note]: TravelNoteIcon,
  [TravelType.rentalCar]: TravelCarIcon,
  [TravelType.rentalHome]: TravelHouseIcon,
  [TravelType.shuttle]: TravelShuttleIcon,
  [TravelType.taxi]: TravelTaxiIcon,
  [TravelType.tent]: TravelCampingIcon,
  [TravelType.train]: TravelTrainIcon,
};

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Travel Item Blocks
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

type TravelComponentProps<T> = Readonly<{ travelItem: T; eventId?: string }>;

type HotelFragmentWithUrlTitleProps = HotelFragment & {
  url: string;
  urlTitle: string;
  isBrannan: boolean;
};

type TravelBlockBaseProps = {
  itemTitle?: Maybe<string>;
  itemType?: Maybe<string>;
  notes?: Maybe<string>;
  address?: Maybe<string>;
  discountCode?: Maybe<string>;
  telephone?: Maybe<string>;
  url?: Maybe<string>;
  urlTitle?: Maybe<string>;
  isBrannan?: boolean;
  children?: React.ReactNode;
};

export const TravelBlockBase: React.FC<TravelBlockBaseProps> = props => {
  const telemetry = useTravelListTelemetry();
  const history = useHistory();
  const { address, children, discountCode, notes, telephone, url, urlTitle, itemTitle, itemType, isBrannan, ...restProps } = props;
  const isLinkV2 = url && urlTitle;
  const isBlockLink = url && !urlTitle;

  const handleClickURL = useCallback(() => {
    telemetry.travelURLClicked(itemType || '', itemTitle || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClickDiscountCode = useCallback(() => {
    telemetry.travelDiscountCodeClicked(itemType || '', itemTitle || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClickTelphone = useCallback(() => {
    telemetry.travelTelephoneClicked(itemType || '', itemTitle || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClickAddress = useCallback(() => {
    telemetry.travelAddressClicked(itemType || '', itemTitle || '');

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <GuestSiteTypographyOverride override={body3ToParagraphOverride}>
      <InfoBlock.Group {...restProps}>
        {(address || notes || telephone || url) && (
          <InfoBlock.Group>
            {notes && (
              <Flex
                width="100%"
                alignItems={'flex-end'}
              >
                <TextV2
                  wordBreak="break-word"
                  tagName="div"
                  typographyVariant={'body3'}
                >
                  <PrettyLink prettyUrlOptions={{ www: false }}>{notes} </PrettyLink>
                </TextV2>
              </Flex>
            )}

            {discountCode && <DiscountCodeBlock handleClick={handleClickDiscountCode}>{discountCode}</DiscountCodeBlock>}
            {isBlockLink && (
              <LinkBlock
                handleClick={handleClickURL}
                url={url}
                ensureProtocol={true}
              >
                {prettyUrl(url, { www: false })}
              </LinkBlock>
            )}
            {isLinkV2 && (
              <LinkV2
                wordBreak="break-word"
                as={'div'}
                cursor={'pointer'}
                href={url as string}
                onClick={() => {
                  if (isBrannan) {
                    withWindow(global => {
                      global.scrollTo(0, 0);
                    });
                  }
                  handleClickURL();
                  history.push(url as string);
                }}
                textAlign={!isBrannan ? 'center' : 'left'}
                isExternal={false}
              >
                <InfoBlock>{urlTitle}</InfoBlock>
              </LinkV2>
            )}
            {telephone && (
              <PhoneBlock
                handleClick={handleClickTelphone}
                phoneNumber={telephone}
              />
            )}
            {address && (
              <MapBlock
                handleClick={handleClickAddress}
                location={address}
                showMap={false}
              />
            )}
          </InfoBlock.Group>
        )}
        {children}
      </InfoBlock.Group>
    </GuestSiteTypographyOverride>
  );
};

type TravelHertzWidgetProps = {
  itemTitle: string;
  itemType: string;
  notes?: Maybe<string>;
  // URL really isn't used, it's a hack to get the Rental-Car Promotion to show up in place of a normal rental car Widget
  url?: Maybe<string>;
};
const TravelHertzWidget: React.FC<TravelHertzWidgetProps> = props => {
  const { itemTitle, itemType, notes } = props;
  const telemetry = useTravelListTelemetry();
  const { t } = useTranslation('guestSiteTravel');
  const { offerTitle, ctaLabel } = t('hertzWidget');
  const { layout } = useLayout();

  const handleClickURL = useCallback(
    (url: string) => {
      telemetry.rentalCarPromotionClicked(itemType, itemTitle, url);
    },
    [itemTitle, itemType, telemetry],
  );

  const { pageFontColor } = useColorPaletteProvider();

  return (
    <Flex
      flexDirection="column"
      borderRadius={4}
      border="1px solid"
      borderColor={pageFontColor}
      overflow="hidden"
    >
      <Flex
        width="100%"
        paddingX={6}
        paddingY={4}
        justifyContent="center"
        alignItems="center"
        borderBottom="1px solid"
        borderColor={pageFontColor}
      >
        <GuestSiteTypographyOverride override={label3ToCaptionOverride}>
          <TextV2 typographyVariant="label3">{offerTitle()}</TextV2>
        </GuestSiteTypographyOverride>
      </Flex>
      <Flex
        flexDirection="column"
        padding={6}
      >
        {notes && (
          <TextV2
            typographyVariant="body3"
            color={pageFontColor}
          >
            {notes}
          </TextV2>
        )}
        <GuestSiteTypographyOverride override={label3ToCaptionOverride}>
          <TextV2
            width="100%"
            typographyVariant="label3"
            color={pageFontColor}
            gap={2}
            marginTop={6}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <SearchIcon size={16} />
            {ctaLabel()}
          </TextV2>
        </GuestSiteTypographyOverride>
        <Box
          display={'grid'}
          gridTemplateColumns={layout === 'aloha' ? '1fr' : '1fr 1fr 1fr'}
          gap={5}
          marginTop={5}
        >
          <BorderButton
            backgroundColor="white"
            target="__blank"
            href={RENTAL_CAR_PROMOTION_LINKS.hertz}
            onClick={() => handleClickURL(RENTAL_CAR_PROMOTION_LINKS.hertz)}
          >
            <HertzLogo64 />
          </BorderButton>
          <BorderButton
            backgroundColor="white"
            target="__blank"
            href={RENTAL_CAR_PROMOTION_LINKS.thrifty}
            onClick={() => handleClickURL(RENTAL_CAR_PROMOTION_LINKS.thrifty)}
          >
            <ThriftyLogo />
          </BorderButton>
          <BorderButton
            backgroundColor="white"
            target="__blank"
            href={RENTAL_CAR_PROMOTION_LINKS.dollar}
            onClick={() => handleClickURL(RENTAL_CAR_PROMOTION_LINKS.dollar)}
          >
            <DollarLogo />
          </BorderButton>
        </Box>
      </Flex>
    </Flex>
  );
};

const BorderButton = (props: React.ComponentProps<typeof LinkV2>) => {
  const { colors } = useTheme();
  return (
    <LinkV2
      display="flex"
      justifyContent="center"
      alignItems="center"
      gap={1}
      boxShadow={`inset 0 0 0 1px ${colors.mono5}`}
      transition={`all ${STANDARD_TRANSITION}`}
      _hover={{
        boxShadow: `inset 0 0 0 2px ${colors.black}`,
      }}
      _active={{
        boxShadow: `inset 0 0 0 2px ${colors.black}`,
      }}
      _focus={{
        boxShadow: `inset 0 0 0 2px ${colors.black}`,
        outline: 'none',
      }}
      borderRadius={'9999px'}
      height={9}
      {...props}
    >
      {props.children}
    </LinkV2>
  );
};
//////////////////////////////////////////////////////////////////////////////////

const TravelFerry: React.FC<TravelComponentProps<FerryFragment>> = ({ travelItem, ...restProps }) => {
  return (
    <TravelBlockBase
      itemTitle={travelItem.name}
      itemType="TravelFerry"
      notes={travelItem.notes}
      url={travelItem.url}
      telephone={travelItem.telephone}
      address={travelItem.address}
      {...restProps}
    />
  );
};

//////////////////////////////////////////////////////////////////////////////////

const TravelFlight: React.FC<TravelComponentProps<FlightFragment>> = ({ travelItem, ...restProps }) => {
  return (
    <TravelBlockBase
      itemTitle={travelItem.name}
      itemType="TravelFlight"
      discountCode={travelItem.code}
      notes={travelItem.notes}
      url={travelItem.url}
      {...restProps}
    />
  );
};

//////////////////////////////////////////////////////////////////////////////////

const TravelHotel: React.FC<TravelComponentProps<HotelFragmentWithUrlTitleProps>> = ({ travelItem, ...restProps }) => {
  return (
    <TravelBlockBase
      itemTitle={travelItem.name}
      itemType="TravelHotel"
      address={travelItem.address}
      notes={travelItem.notes}
      telephone={travelItem.telephone}
      url={travelItem.url}
      urlTitle={travelItem.urlTitle}
      isBrannan={travelItem.isBrannan}
      {...restProps}
    />
  );
};

//////////////////////////////////////////////////////////////////////////////////

const TravelList: React.FC<TravelComponentProps<EventTravelListFragment>> = ({ travelItem, eventId, ...restProps }) => {
  const telemetry = useTravelListTelemetry();

  const handleClickURL = useCallback(
    url => {
      telemetry.travelURLClicked('TravelList', travelItem.name, url);
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [travelItem],
  );

  if (!travelItem.items.length) {
    return null;
  }

  return (
    <TravelBlockBase
      itemType="TravelList"
      itemTitle={travelItem.name}
      {...restProps}
    >
      <InfoBlock.Group>
        {travelItem.items.map(({ id, title, url }) => {
          if (!title && !url) {
            return null;
          }

          const content = title || url;
          return (
            <ListItemBlock
              key={id}
              data-testid={id}
            >
              {url ? (
                <SafeLink
                  wordBreak="break-word"
                  onClick={() => handleClickURL(url)}
                  isExternal={true}
                  href={url}
                  display="block"
                  ensureProtocol={true}
                >
                  {content}
                </SafeLink>
              ) : (
                <PrettyLink tagName="span">{content}</PrettyLink>
              )}
            </ListItemBlock>
          );
        })}
      </InfoBlock.Group>
    </TravelBlockBase>
  );
};

//////////////////////////////////////////////////////////////////////////////////

const TravelNote: React.FC<TravelComponentProps<EventTravelNoteFragment>> = ({ travelItem }) => {
  return (
    <TravelBlockBase
      itemTitle={travelItem.name}
      itemType="TravelNote"
      notes={travelItem.notes}
      url={travelItem.url}
    />
  );
};

//////////////////////////////////////////////////////////////////////////////////

const TravelRentalCar: React.FC<TravelComponentProps<RentalCarFragment>> = props => {
  const { notes, address, telephone, url, name } = props.travelItem;
  const isHertzLink = isRentalCarPromotionLink(url || '');

  return isHertzLink ? (
    <TravelHertzWidget
      itemTitle={name}
      itemType="TravelRentalCar"
      notes={notes}
      url={url}
    />
  ) : (
    <TravelBlockBase
      itemTitle={name}
      itemType="TravelRentalCar"
      address={address}
      notes={notes}
      telephone={telephone}
      url={url}
    />
  );
};

//////////////////////////////////////////////////////////////////////////////////

const TravelRentalHome: React.FC<TravelComponentProps<RentalHomeFragment>> = props => {
  const { notes, address, telephone, url, name } = props.travelItem;
  return (
    <TravelBlockBase
      itemTitle={name}
      itemType="TravelHotel"
      address={address}
      notes={notes}
      telephone={telephone}
      url={url}
    />
  );
};

//////////////////////////////////////////////////////////////////////////////////

/**
 * Formats shuttle time range using locale-aware time formatting.
 * Uses formatDateForLanguage with 'hh:mm' for proper 12/24-hour format based on locale.
 */
export const formatShuttleTimeRange = (language: string, startTimeMs: number, endTimeMs: number | undefined, timezone: string): string => {
  const startFormatted = formatDateForLanguage(language, new Date(startTimeMs), 'hh:mm', { timezone });
  if (!endTimeMs) {
    return startFormatted ?? '';
  }
  const endFormatted = formatDateForLanguage(language, new Date(endTimeMs), 'hh:mm', { timezone });
  return [startFormatted, endFormatted].filter(Boolean).join(' – ');
};

const LowercaseText = styled(TextV2)`
  text-transform: lowercase;
`;

const TravelShuttle: React.FC<TravelComponentProps<ShuttleFragment>> = React.memo(props => {
  const telemetry = useTravelListTelemetry();
  const { notes, items, name } = props.travelItem;
  const itemCount = items.length;

  const { pageFontColor } = useColorPaletteProvider();
  const dividerBorderColor = pageFontColor ? { borderColor: pageFontColor } : {};

  const handleClickURL = useCallback(
    (url: string) => {
      telemetry.travelURLClicked('TravelShuttle', name, url);
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [name],
  );

  const { language } = useEventLocale();

  return (
    <TravelBlockBase notes={notes}>
      <InfoBlock.Group>
        {/* {name && <Text>{name}</Text>} */}
        {items.map((item, index) => {
          const { id, startTime, endTime, note, url } = item;
          if (!startTime) {
            return null;
          }

          return (
            <GuestSiteTypographyOverride
              key={id}
              override={body2ToParagraphOverride}
            >
              <InfoBlock.Group data-testid={`shuttle-item-${id}`}>
                <Flex alignItems="center">
                  <Calendar />
                  <TextV2
                    wordBreak="break-word"
                    data-testid="shuttle-date"
                    marginLeft={4}
                  >
                    {formatDateForLanguage(language, new Date(startTime.milliseconds), 'MMMdd', { timezone: startTime.timezone || 'America/Los_Angeles' })}
                  </TextV2>
                </Flex>

                <Flex alignItems="center">
                  <Clock />
                  <LowercaseText
                    data-testid="shuttle-timeframe"
                    marginLeft={4}
                  >
                    {formatShuttleTimeRange(language, startTime.milliseconds, endTime?.milliseconds, startTime.timezone || 'America/Los_Angeles')}
                  </LowercaseText>
                </Flex>
                {note && (
                  <TextV2
                    wordBreak="break-word"
                    tagName="div"
                  >
                    <PrettyLink>{note}</PrettyLink>
                  </TextV2>
                )}
                {url && (
                  <GuestSiteTypographyOverride
                    key={id}
                    override={body3ToParagraphOverride}
                  >
                    <LinkBlock
                      handleClick={() => handleClickURL(url)}
                      url={url}
                      ensureProtocol={true}
                    >
                      {prettyUrl(url)}
                    </LinkBlock>
                  </GuestSiteTypographyOverride>
                )}
                {index + 1 < itemCount && <Divider {...dividerBorderColor} />}
              </InfoBlock.Group>
            </GuestSiteTypographyOverride>
          );
        })}
      </InfoBlock.Group>
    </TravelBlockBase>
  );
});

//////////////////////////////////////////////////////////////////////////////////

const TravelTent: React.FC<TravelComponentProps<TentFragment>> = ({ travelItem, ...restProps }) => {
  return (
    <TravelBlockBase
      itemTitle={travelItem.name}
      itemType="TravelRent"
      address={travelItem.address}
      notes={travelItem.notes}
      telephone={travelItem.telephone}
      url={travelItem.url}
      {...restProps}
    />
  );
};

//////////////////////////////////////////////////////////////////////////////////

const TravelTaxi: React.FC<TravelComponentProps<TaxiFragment>> = ({ travelItem, ...restProps }) => {
  return (
    <TravelBlockBase
      itemTitle={travelItem.name}
      itemType="TravelTaxi"
      address={travelItem.address}
      notes={travelItem.notes}
      telephone={travelItem.telephone}
      url={travelItem.url}
      {...restProps}
    />
  );
};
//////////////////////////////////////////////////////////////////////////////////

const TravelTrain: React.FC<TravelComponentProps<TrainFragment>> = ({ travelItem, ...restProps }) => {
  return (
    <TravelBlockBase
      itemTitle={travelItem.name}
      itemType="TravelTrain"
      address={travelItem.address}
      notes={travelItem.notes}
      telephone={travelItem.telephone}
      url={travelItem.url}
      {...restProps}
    />
  );
};

//////////////////////////////////////////////////////////////////////////////////

export const TravelBooking: React.FC<TravelComponentProps<EventTravelBookingTypeFragment>> = ({ travelItem }) => {
  return (
    <Booking
      address={travelItem.address}
      placeId={travelItem.placeId}
      timezone={travelItem.timezone}
      startTimeMs={travelItem.startTime?.milliseconds}
      endTimeMs={travelItem.endTime?.milliseconds}
    />
  );
};

//////////////////////////////////////////////////////////////////////////////////

// I don't know how to automatically infer the travelItem type
export const travelTypeToComponent = {
  // TODO: Add Booking Assistant after creating input + datepicker
  // [TravelType.booking]: TravelBooking,
  [TravelType.list]: TravelList,
  [TravelType.flight]: TravelFlight,
  [TravelType.ferry]: TravelFerry,
  [TravelType.hotel]: TravelHotel,
  [TravelType.note]: TravelNote,
  [TravelType.rentalCar]: TravelRentalCar,
  [TravelType.rentalHome]: TravelRentalHome,
  [TravelType.shuttle]: TravelShuttle,
  [TravelType.taxi]: TravelTaxi,
  [TravelType.tent]: TravelTent,
  [TravelType.train]: TravelTrain,
};
